import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { formatToDisplayDate, formatToISODate, parseDisplayDate } from "../services/dateFormatter";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import DateInput from "../components/DateInput";

const EMPTY = {
  customer: "",
  start_date: "",
  end_date: "",
  value: "",
  installment: "Full",
  payment_mode: "Advance",
  generate_invoices: false,
};

export default function AMCContracts({ role }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
  const [agreementFilter, setAgreementFilter] = useState(() => searchParams.get("agreement") || "");
  const [customerFilter, setCustomerFilter] = useState(() => searchParams.get("customer") || "");
  const [installmentFilter, setInstallmentFilter] = useState(() => searchParams.get("installment") || "");
  const [paymentModeFilter, setPaymentModeFilter] = useState(() => searchParams.get("payment_mode") || "");
  const [endDateFrom, setEndDateFrom] = useState(() => searchParams.get("end_date_after") || "");
  const [endDateTo, setEndDateTo] = useState(() => searchParams.get("end_date_before") || "");
  const [minValue, setMinValue] = useState(() => searchParams.get("min_value") || "");
  const [maxValue, setMaxValue] = useState(() => searchParams.get("max_value") || "");
  const [page, setPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);

  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showExpired, setShowExpired] = useState(false);  // show past/expired contracts
  const pageSize = 12;
  const [totalContracts, setTotalContracts] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [similarContracts, setSimilarContracts] = useState([]);
  const [showSimilarContractWarning, setShowSimilarContractWarning] = useState(false);
  const [pendingContractPayload, setPendingContractPayload] = useState(null);
  const [descriptionFilter, setDescriptionFilter] = useState(() => searchParams.get("description") || "");
  const [ordering, setOrdering] = useState(() => searchParams.get("ordering") || "");

  const toggleOrdering = field => {
    setOrdering(curr => {
      if (curr === field) return `-${field}`;
      if (curr === `-${field}`) return '';
      return field;
    });
  };

  const sortIndicator = field => ordering === field ? '↑' : ordering === `-${field}` ? '↓' : '↕';
  const renderSortHeading = (label, field) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.35rem' }}>
      <span>{label}</span>
      <button type="button" onClick={() => toggleOrdering(field)} style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '.9rem' }}>
        {sortIndicator(field)}
      </button>
    </div>
  );

  const buildQuery = (p = page) => {
    const parts = [`page=${p}`, `page_size=${pageSize}`];
    if (searchTerm) parts.push(`search=${encodeURIComponent(searchTerm)}`);
    if (agreementFilter) {
      const clean = agreementFilter.replace(/^AMC-?/i, '').trim();
      if (clean) parts.push(`agreement=${encodeURIComponent(clean)}`);
    }
    if (customerFilter) parts.push(`customer=${encodeURIComponent(customerFilter)}`);
    if (installmentFilter) parts.push(`installment=${encodeURIComponent(installmentFilter)}`);
    if (descriptionFilter) parts.push(`description=${encodeURIComponent(descriptionFilter)}`);
    if (paymentModeFilter) parts.push(`payment_mode=${encodeURIComponent(paymentModeFilter)}`);
    const endDateFromIso = parseDisplayDate(endDateFrom);
    const endDateToIso = parseDisplayDate(endDateTo);
    if (endDateFromIso) parts.push(`end_date_after=${encodeURIComponent(endDateFromIso)}`);
    if (endDateToIso) parts.push(`end_date_before=${encodeURIComponent(endDateToIso)}`);
    if (minValue) parts.push(`min_value=${encodeURIComponent(minValue)}`);
    if (maxValue) parts.push(`max_value=${encodeURIComponent(maxValue)}`);
    if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
    if (showExpired) parts.push(`show_expired=true`);
    return parts.join("&");
  };

  const syncUrl = (nextPage = page, nextSearch = searchTerm, nextInstallment = installmentFilter) => {
    const params = {};
    if (nextSearch) params.q = nextSearch;
    if (agreementFilter) params.agreement = agreementFilter;
    if (customerFilter) params.customer = customerFilter;
    if (descriptionFilter) params.description = descriptionFilter;
    if (nextInstallment) params.installment = nextInstallment;
    if (paymentModeFilter) params.payment_mode = paymentModeFilter;
    if (endDateFrom) params.end_date_after = endDateFrom;
    if (endDateTo) params.end_date_before = endDateTo;
    if (minValue) params.min_value = minValue;
    if (maxValue) params.max_value = maxValue;
    if (ordering) params.ordering = ordering;
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params, { replace: true });
  };

  const load = (p = page) => {
    api.get(`amc/?${buildQuery(p)}`)
      .then(r => {
        const data = r.data || {};
        setContracts(data.results || []);
        setTotalContracts(data.count || (data.results ? data.results.length : 0));
      })
      .catch(() => {});

    api.get(`customers/?page_size=2000`)
      .then(r => setCustomers(r.data.results || r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load(page);
  }, [page, searchTerm, agreementFilter, customerFilter, descriptionFilter, installmentFilter, paymentModeFilter, endDateFrom, endDateTo, minValue, maxValue, ordering, showDeleted, showExpired]);

  useEffect(() => {
    syncUrl(page, searchTerm, installmentFilter);
  }, [page, searchTerm, agreementFilter, customerFilter, descriptionFilter, installmentFilter, paymentModeFilter, endDateFrom, endDateTo, minValue, maxValue, ordering]);

  const filteredCustomers = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter(c => c.name.toLowerCase().includes(query));
  }, [customerQuery, customers]);

  const openCreate = () => {
    setForm(EMPTY);
    setEditId(null);
    setModal("form");
  };

  const openEdit = c => {
    setForm({
      customer: c.customer,
      start_date: formatToDisplayDate(c.start_date),
      end_date: formatToDisplayDate(c.end_date),
      value: c.value,
      installment: c.installment,
      payment_mode: c.payment_mode,
      generate_invoices: false,
    });
    setEditId(c.id);
    setModal("form");
  };

  const openView = c => {
    setViewData(c);
    setModal("view");
  };

  const close = () => {
    setModal(null);
    setViewData(null);
  };

  const save = async e => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (editId) delete payload.generate_invoices;

      const startDateIso = parseDisplayDate(payload.start_date);
      const endDateIso = parseDisplayDate(payload.end_date);
      if (!startDateIso || !endDateIso) {
        alert('Please enter Start Date and Expiration Date in DD/MM/YYYY format.');
        return;
      }
      payload.start_date = startDateIso;
      payload.end_date = endDateIso;

      // similarity check for contracts
      try {
        const res = await api.post('amc/check-similar/', {
          customer_id: payload.customer,
          value: payload.value,
          start_date: payload.start_date,
        });
        const similar = res.data.similar_contracts || [];
        if (similar.length > 0) {
          setSimilarContracts(similar);
          // Include editId so the 'Proceed Anyway' action can decide between POST and PUT
          setPendingContractPayload({ payload, editId });
          setShowSimilarContractWarning(true);
          return;
        }
      } catch (_) {}

      editId ? await api.put(`amc/${editId}/`, payload) : await api.post("amc/", payload);
      close();
      load();
    } catch (err) {
      const data = err?.response?.data;
      if (!data) {
        alert(
          "Cannot connect to backend.\n\nMake sure the Django server is running:\npython manage.py runserver 8080"
        );
        return;
      }
      if (typeof data === "string" && data.includes("<!DOCTYPE")) {
        alert(
          "Database tables are missing.\n\nRun this in your backend terminal:\n\npython manage.py migrate\n\nThen restart: python manage.py runserver 8080"
        );
        return;
      }
      alert("Validation error:\n\n" + JSON.stringify(data, null, 2));
    }
  };

  const del = async id => {
    if (!window.confirm("Delete this AMC contract?")) return;
    try {
      await api.delete(`amc/${id}/`);
      load();
    } catch {
      alert("Delete failed. Check that the backend is running.");
    }
  };

  const restore = async id => {
    if (!window.confirm("Restore this AMC contract?")) return;
    try {
      await api.post(`amc/${id}/restore/`);
      alert("AMC contract restored successfully");
      load(page);
    } catch (err) {
      alert("Restore failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const fmt = n => "₹" + parseFloat(n).toLocaleString("en-IN");
  const isAdmin = role === "Admin";

  const custName = id => customers.find(c => c.id === id)?.name || "—";

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Annual Maintenance Contracts</h2>
          <p>Configure contract durations, intervals, and baseline contract fees.</p>
        </div>
        {isAdmin && (
          <button className="btn btn-purple" onClick={openCreate}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Create AMC Agreement
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: ".5rem", marginBottom: ".75rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Global search..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          style={{ padding: ".65rem", minWidth: 220, flex: 1, maxWidth: 320 }}
        />
        <select
          value={installmentFilter}
          onChange={e => {
            setInstallmentFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Installments</option>
          <option value="Full">Full</option>
          <option value="Half-Yearly">Half-Yearly</option>
          <option value="Quarterly">Quarterly</option>
          <option value="Monthly">Monthly</option>
        </select>
        <select
          value={paymentModeFilter}
          onChange={e => {
            setPaymentModeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Payment Modes</option>
          <option value="Advance">Advance</option>
          <option value="After Completion">After Completion</option>
        </select>
        <button className="btn btn-slate btn-sm" onClick={() => load(page)}>
          Refresh
        </button>
        <button
          className="btn btn-slate btn-sm"
          onClick={() => {
            setSearchTerm("");
            setAgreementFilter("");
            setCustomerFilter("");
            setInstallmentFilter("");
            setPaymentModeFilter("");
            setEndDateFrom("");
            setEndDateTo("");
            setMinValue("");
            setMaxValue("");
            setOrdering("");
            setPage(1);
          }}
        >
          Clear Filters
        </button>
        {isAdmin && <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .75rem', background: '#334155', borderRadius: '.25rem', fontSize: '.875rem' }}>
          <input type="checkbox" checked={showDeleted} onChange={e => { setShowDeleted(e.target.checked); setPage(1); }} style={{ cursor: 'pointer' }} />
          Show Deleted
        </label>}
        <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .75rem', background: '#334155', borderRadius: '.25rem', fontSize: '.875rem' }}>
          <input type="checkbox" checked={showExpired} onChange={e => { setShowExpired(e.target.checked); setPage(1); }} style={{ cursor: 'pointer' }} />
          Show Expired
        </label>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{renderSortHeading('Agreement No', 'id')}</th>
              <th>{renderSortHeading('Customer', 'customer__name')}</th>
              <th colSpan="2">{renderSortHeading('Expiration / Terms', 'end_date')}</th>
              <th>{renderSortHeading('Description', 'description')}</th>
              <th>{renderSortHeading('Base Valuation', 'value')}</th>
              <th className="th-right">Actions</th>
            </tr>
            <tr>
              <th>
                <input
                  type="text"
                  placeholder="Agreement #"
                  value={agreementFilter}
                  onChange={e => { setAgreementFilter(e.target.value); setPage(1); }}
                />
              </th>
              <th>
                <input
                  type="text"
                  placeholder="Customer"
                  value={customerFilter}
                  onChange={e => { setCustomerFilter(e.target.value); setPage(1); }}
                />
              </th>
              <th colSpan="2" style={{ padding: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="date-range-row">
                      <div className="date-range-part">
                        <DateInput value={endDateFrom} onChange={v => { setEndDateFrom(v); setPage(1); }} placeholder="From (DD/MM/YYYY)" />
                      </div>
                      <span className="date-range-separator">–</span>
                      <div className="date-range-part">
                        <DateInput value={endDateTo} onChange={v => { setEndDateTo(v); setPage(1); }} placeholder="To (DD/MM/YYYY)" />
                      </div>
                    </div>
                  </div>
                  <div style={{ minWidth: 100 }}>
                    <select value={installmentFilter} onChange={e => { setInstallmentFilter(e.target.value); setPage(1); }} style={{ width: '100%' }}>
                      <option value="">Any</option>
                      <option value="Full">Full</option>
                      <option value="Half-Yearly">Half-Yearly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              </th>
              <th>
                <input
                  type="text"
                  placeholder="Description"
                  value={descriptionFilter}
                  onChange={e => { setDescriptionFilter(e.target.value); setPage(1); }}
                />
              </th>
              <th>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={minValue}
                    onChange={e => { setMinValue(e.target.value); setPage(1); }}
                    style={{ width: '100%' }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxValue}
                    onChange={e => { setMaxValue(e.target.value); setPage(1); }}
                    style={{ width: '100%' }}
                  />
                </div>
              </th>
              <th className="th-right"></th>
            </tr>
          </thead>
          <tbody>
              {contracts.length === 0 && (
              <tr>
                <td colSpan="7">
                  <div className="empty-state">No AMC contracts registered yet.</div>
                </td>
              </tr>
            )}
            {contracts.map(c => (
              <tr key={c.id} style={c.is_live === false ? { opacity: 0.7 } : {}}>
                <td style={{ fontWeight: 700, color: c.is_live === false ? "#94a3b8" : "#c084fc" }}>AMC-{c.id}</td>
                <td style={{ color: "#f1f5f9" }}>{c.customer_name || custName(c.customer)}</td>
                <td colSpan="2" className="date-cell" style={{ color: "#94a3b8" }}>
                  <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>{formatToDisplayDate(c.end_date)}</span>
                    <span className="badge badge-purple" style={{ marginRight: 0 }}>
                      {c.installment}
                    </span>
                    <span className="badge badge-slate">{c.payment_mode}</span>
                    {c.is_live === false && (
                      <span className="badge badge-red" style={{ marginRight: 0 }}>Expired</span>
                    )}
                  </div>
                </td>
                <td style={{ color: '#cbd5e1', maxWidth: 400, wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{c.description || ''}</td>
                <td className="amount-cell" style={{ color: "#34d399", fontWeight: 600 }}>{fmt(c.value)}</td>
                <td className="td-right">
                  <div className="actions-cell">
                    <button className="btn btn-slate btn-sm" onClick={() => openView(c)}>
                      View
                    </button>
                    {isAdmin && (
                      <button className="btn btn-purple btn-sm" onClick={() => openEdit(c)}>
                        Edit
                      </button>
                    )}
                    {isAdmin && (
                      <button className="btn btn-red btn-sm" onClick={() => del(c.id)}>
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        setPage={setPage}
        total={totalContracts}
        pageSize={pageSize}
      />

      {modal === "form" && (
        <Modal title={editId ? "EDIT AMC AGREEMENT" : "CONFIGURE AMC AGREEMENT"} titleColor="purple" onClose={close}>
          <form className="form-grid" onSubmit={save}>
            <div className="field full-width">
              <label>Target Client Entity</label>
              <input
                type="text"
                list="amc-customer-list"
                placeholder="Search or select customer..."
                value={customerQuery}
                onChange={e => {
                  const query = e.target.value;
                  setCustomerQuery(query);
                  const selected = customers.find(c => c.name === query);
                  if (selected && form.customer !== String(selected.id)) {
                    setForm({ ...form, customer: selected.id });
                  }
                }}
                style={{ width: '100%' }}
              />
              <datalist id="amc-customer-list">
                {filteredCustomers.map(c => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Start Date</label>
                <DateInput value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} placeholder="DD/MM/YYYY" />
              </div>
              <div className="field">
                <label>Expiration End Date</label>
                <DateInput value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} placeholder="DD/MM/YYYY" />
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Payment Installment</label>
                <select value={form.installment} onChange={e => setForm({ ...form, installment: e.target.value })}>
                  {["Monthly", "Quarterly", "Half-Yearly", "Full"].map(o => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Payment Timing Mode</label>
                <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })}>
                  {["Advance", "After Completion"].map(o => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {!editId && (
              <div className="field full-width">
                <label style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
                  <input
                    type="checkbox"
                    checked={form.generate_invoices}
                    onChange={e => setForm({ ...form, generate_invoices: e.target.checked })}
                  />
                  Generate invoices for all billing slots (leave invoice number/date blank)
                </label>
              </div>
            )}
            <div className="field full-width">
              <label>Total Baseline Value (Annual ₹)</label>
              <input
                type="number"
                required
                value={form.value}
                onChange={e => setForm({ ...form, value: e.target.value })}
                placeholder="e.g. 80000"
              />
            </div>
            <div className="field full-width">
              <label>Description / Notes</label>
              <textarea
                value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Add any additional terms, conditions, or notes for this agreement..."
                style={{ width: '100%', minHeight: '100px', padding: '.6rem 1rem', border: '1px solid var(--input-border)', borderRadius: '.75rem', backgroundColor: 'var(--input-bg)', color: 'var(--text)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '.875rem', outline: 'none' }}
              />
            </div>
            <button type="submit" className="btn btn-purple" style={{ width: "100%", justifyContent: "center", padding: ".7rem" }}>
              Register Contract
            </button>
          </form>
        </Modal>
      )}

      {showSimilarContractWarning && (
        <Modal title="Similar Contracts Found" titleColor="amber" onClose={() => setShowSimilarContractWarning(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
            <div style={{ color: '#94a3b8' }}>
              {pendingContractPayload && pendingContractPayload.editId
                ? 'Agreement will be modified. Confirm to save changes.'
                : 'Similar contracts for this customer were found. Review before proceeding.'}
            </div>
            <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '.5rem', padding: '.5rem', background: 'var(--surface)' }}>
              {similarContracts.map(s => (
                <div key={s.id} style={{ padding: '.5rem', borderBottom: '1px solid var(--border-soft)' }}>
                  <div style={{ fontWeight: 700 }}>{s.amc_display}</div>
                  <div style={{ color: '#94a3b8' }}>{formatToDisplayDate(s.start_date)} → {formatToDisplayDate(s.end_date)} • {fmt(s.value)}</div>
                  <div style={{ color: '#cbd5e1', fontSize: '.85rem' }}>Reasons: {s.reasons.join(', ')}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-slate" onClick={() => { setShowSimilarContractWarning(false); setPendingContractPayload(null); }}>Cancel</button>
              <button className="btn btn-purple" onClick={async () => {
                try {
                  if (pendingContractPayload) {
                    const { payload: pl, editId: pendingEdit } = pendingContractPayload;
                    if (pendingEdit) {
                      await api.put(`amc/${pendingEdit}/`, pl);
                    } else {
                      await api.post('amc/', pl);
                    }
                  }
                } catch (_) {}
                setShowSimilarContractWarning(false);
                setPendingContractPayload(null);
                close();
                load();
              }}>Proceed Anyway</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "view" && viewData && (
        <Modal title="RECORD PREVIEW AUDIT" titleColor="amber" onClose={close} footer={
          <div style={{ display: "flex", gap: ".5rem" }}>
            {viewData.is_deleted && isAdmin && <button className="btn btn-green btn-sm" onClick={() => { restore(viewData.id); close(); }}>Restore</button>}
            <button className="btn btn-slate btn-sm" onClick={close}>
              Dismiss View
            </button>
          </div>
        }>
          <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
            {viewData.is_deleted && <div style={{ padding: ".5rem", background: "#7c2d12", borderRadius: ".25rem", color: "#fed7aa", fontSize: ".875rem" }}>
              ⚠️ This record was deleted on {new Date(viewData.deleted_at).toLocaleString()}
            </div>}
            {[
              ["Agreement No", `AMC-${viewData.id}`],
              ["Client", viewData.customer_name || custName(viewData.customer)],
              ["Period", `${formatToDisplayDate(viewData.start_date)} → ${formatToDisplayDate(viewData.end_date)}`],
              ["Installment", viewData.installment],
              ["Timing", viewData.payment_mode],
              ["Base Value", fmt(viewData.value)],
            ].map(([k, v]) => (
              <div key={k} className="detail-row">
                <span className="dk purple">{k}:</span>
                <span style={{ color: "#cbd5e1" }}>{v}</span>
              </div>
            ))}
            {viewData.description && (
              <>
                <div className="detail-divider"></div>
                <div className="detail-row">
                  <span className="dk purple">Description:</span>
                  <span style={{ color: "#cbd5e1", whiteSpace: "pre-wrap", wordWrap: "break-word", overflowWrap: "break-word" }}>{viewData.description}</span>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
